/**
 * UC-10: EnforcePolicies
 * TOGAF Application ABB — runs policy enforcement and computes game effects without UI deps.
 */
import type { Policy, PolicyChallenge, PolicyGameEffect } from '../../../types/policy';
import { PolicyStatus } from '../../../types/policy';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { IPolicyEnforcement } from '../../domain/ports/IPolicyEnforcement';
import type { IPolicyManager } from '../../domain/ports/IPolicyManager';

export interface EnforcePoliciesDependencies {
  policyEnforcement: IPolicyEnforcement;
  policyManager: IPolicyManager;
  logger?: IAppLogger;
}

export interface EnforcePoliciesOutput {
  policies: Policy[];
  challenges: PolicyChallenge[];
  policyEffects: PolicyGameEffect[];
}

export class EnforcePoliciesUseCase {
  constructor(private readonly deps: EnforcePoliciesDependencies) {}

  execute(): EnforcePoliciesOutput {
    this.deps.policyEnforcement.enforcePolicies();

    const policies = this.deps.policyManager.getPolicies();
    const challenges = this.deps.policyManager.getChallenges();

    const policyEffects: PolicyGameEffect[] = [];
    for (const policy of policies) {
      if (policy.status === PolicyStatus.Enforced) {
        policyEffects.push(this.deps.policyManager.calculateGameEffects(policy.id));
      }
    }

    this.deps.logger?.log('debug', 'policies.enforced', {
      policyCount: policies.length,
      enforcedCount: policyEffects.length,
      activeChallenges: challenges.filter((c) => c.isActive).length,
    });

    return { policies, challenges, policyEffects };
  }
}